import React, { useState } from "react";
import { Alert, Badge, Button, Drawer, Modal, Tabs } from "antd";

import { useAuth } from "../../../../../store/AuthContext/AuthContext";
import { useFetch } from "../../../../../hooks/useFetch/useFetch";
import { DeleteOutlined, EditOutlined, FileOutlined, PlusOutlined } from "@ant-design/icons";
import { ButtonLZ } from "../../../../components/design/Buttons/ButtonLZ";
import { t } from "i18next";

import { AddReportDefinitionModal } from "./definition/AddReportDefinitionModal";
import { EditReportDefinitionModal } from "./definition/EditReportDefinitionModal";
import { PREDEFINED_REPORTS} from "../../../menuUsers/reports/ReportTypes";
import { customRequest } from "../../../../../common/request/customRequest";
import { STATUS_CODE } from "../../../../../common/constants/statusCode";
import { SpinLZOnTop } from "../../../../components/design/Spinner/SpinLZ";
import { ReportType } from "../../../../components/design/reports/ReportType";
import { DrawerPanelTitle, drawerPanelStyles } from "../../../common/Drawers/DrawerPanelTitle";
import { EmptyStateBox } from "../../../../components/design/EmptyStateBox/EmptyStateBox";

export const ReportDefinitionsDrawer = (props: { onFinish: any }) => {    
                        
    const [updateData, setUpdateData] = useState(false);	    
    const [scheduledReports, setScheduledReports] = useState<any>(null);
    const [addReportDefinitionModal, setAddReportDefinitionModal] = useState(false);
    const [editReportDefinitionModal, setEditReportDefinitionModal] = useState(null);
    const [changes, setChanges] = useState(false);
    const [errorMessage, setErrorMessage] = useState<any>(null);    
    const { projectGroup } = useAuth();    	
    
    const { isLoading }: any = useFetch(
        `${process.env.REACT_APP_API}/projects/${projectGroup.project}/scheduledReports` ,
        {},
        [updateData],
        (data) => {
            setScheduledReports(data);          
        }
    );

    const deleteScheduledReport = async (scheduledReportId: string): Promise<void> => {
        
        const response = await customRequest(`${process.env.REACT_APP_API}/projects/${projectGroup.project}/scheduledReports/${scheduledReportId}`,{ method: 'DELETE'});
        if (response?.statusCode === STATUS_CODE.BAD_REQUEST) {
			setErrorMessage(response.message);			
		} else if (response?.statusCode === STATUS_CODE.OK) {			            
                setErrorMessage(null);
                setUpdateData(!updateData);        
                setChanges(true);
		}                  
    }

    const projectReports = scheduledReports?.filter(
        (row: any) => PREDEFINED_REPORTS[row.type]?.scope === 'PROJECT'
    ) || [];
    const locationReports = scheduledReports?.filter(
        (row: any) => PREDEFINED_REPORTS[row.type]?.scope === 'LOCATION'
    ) || [];

    const renderReportRow = (row: any) => (
        <div key={row.id} style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 100px',
            alignItems: 'center',
            marginBottom: 8,
            gap: 24,
        }}>
            <ReportType
                reportType={row.type}
                scope={true}
            />
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                flexWrap: 'wrap',
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                background: 'transparent',
            }}>
                {row.scheduled && (
                    <>
                        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Periodo
                            </span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                                {row.scheduled?.schedule_type === 'weekly' ? 'Semanal' : 'Mensual'}
                            </span>
                        </span>
                        <span style={{ width: 1, alignSelf: 'stretch', background: '#e2e8f0', minHeight: 28 }} />
                        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Envío de emails
                            </span>
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontSize: 13,
                                fontWeight: 600,
                                color: row.scheduled?.enabled ? 'dodgerblue' : '#64748b',
                            }}>
                                <Badge status={row.scheduled?.enabled ? 'processing' : 'default'} />
                                {row.scheduled?.enabled ? 'Automatizado' : 'Manual'}
                            </span>
                        </span>
                    </>
                )}
                <EditOutlined style={{ cursor: 'pointer', color: '#94a3b8', marginLeft: 'auto' }} onClick={() => setEditReportDefinitionModal(row)}/>
            </div>
            <Button
                shape='round'
                size='small'
                type='link'
                style={{ color: 'grey', justifySelf: 'end' }}
                onClick={() => {
                    Modal.confirm({
                        title: 'Confirmas que quieres quitar este informe',
                        okText: t('dictionary.confirm'),
                        cancelText: t('dictionary.cancel'),
                        onOk: () => deleteScheduledReport(row.id)
                    })
                }}
            >
                <DeleteOutlined style={{ fontSize: 12 }} />
                <small> Quitar Informe</small>
            </Button>
        </div>
    );

    const renderTabContent = (hint: string, rows: any[]) => (
        <div style={{ paddingTop: 8 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>{hint}</div>
            {rows.length === 0 ? (
                <EmptyStateBox
                    subtle
                    icon={<FileOutlined />}
                    title="Ninguno activo"
                    description="Añade un informe de este tipo para compartirlo por email"
                />
            ) : (
                rows.map(renderReportRow)
            )}
        </div>
    );

    return (
        <Drawer
            placement="right"
            width={'60%'}
            open={true}
            onClose={() => props.onFinish(changes)}
            {...drawerPanelStyles}
            bodyStyle={{ ...drawerPanelStyles.bodyStyle, padding: '0px 10px' }}
            title={
				<DrawerPanelTitle
					title="Mis Informes"
					isProject={true}
				/>
			}>	      
            {isLoading && <SpinLZOnTop/>}     
            <div style={{margin:'40px'}}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonLZ
                        type="default"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setAddReportDefinitionModal(true)}
                    >
                        Añadir Informe
                    </ButtonLZ>
                </div>

                {scheduledReports && (
                    <Tabs
                        defaultActiveKey="project"
                        items={[
                            {
                                key: 'project',
                                label: (
                                    <Badge
                                        count={projectReports.length}
                                        showZero={false}
                                        size="small"
                                        offset={[8, 0]}
                                        style={{
                                            backgroundColor: '#3b82f6',
                                            color: '#fff',
                                            fontWeight: 600,
                                            boxShadow: '0 1px 3px rgba(59, 130, 246, 0.35)',
                                        }}
                                    >
                                        <span>Proyecto</span>
                                    </Badge>
                                ),
                                children: renderTabContent(
                                    'Resumen de todos los locales del proyecto, pensados para el responsable de marketing o perfiles similares',
                                    projectReports
                                ),
                            },
                            {
                                key: 'location',
                                label: (
                                    <Badge
                                        count={locationReports.length}
                                        showZero={false}
                                        size="small"
                                        offset={[8, 0]}
                                        style={{
                                            backgroundColor: '#3b82f6',
                                            color: '#fff',
                                            fontWeight: 600,
                                            boxShadow: '0 1px 3px rgba(59, 130, 246, 0.35)',
                                        }}
                                    >
                                        <span>Local</span>
                                    </Badge>
                                ),
                                children: renderTabContent(
                                    'Informes específicos de cada negocio, orientados al director de oficina o al responsable del local',
                                    locationReports
                                ),
                            },
                        ]}
                    />
                )}

                {errorMessage && <Alert
					type='error'
					message={errorMessage}
					banner
				/>
			    }
            </div>
            {addReportDefinitionModal && 
                <AddReportDefinitionModal 
                    onFinish={() => {setAddReportDefinitionModal(false);}}
                    onChange={() => {setAddReportDefinitionModal(false); setChanges(true); setUpdateData(!updateData)}}/>
            }            
            {editReportDefinitionModal && 
                <EditReportDefinitionModal 
                    scheduleReport={editReportDefinitionModal} 
                    onFinish={() => {setEditReportDefinitionModal(null);}}
                    onChange={() => {setEditReportDefinitionModal(null); setChanges(true); setUpdateData(!updateData)}}/>
            }
        </Drawer>            
    );
};
